import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Tree, Dropdown, Input } from "antd";
import { FileOutlined, FolderOutlined, MoreOutlined } from "@ant-design/icons";
import type { TreeProps } from "antd";
import type { DataNode } from "antd/es/tree";
import clsx from "clsx";
import type {
  FolderTreeData,
  FolderTreeProps,
  EditNodeInfo,
  CreateNodeContext,
} from "./types";
import { useStyles } from "./styles";
import { useFolderDrag, type DropPosition } from "./useFolderDrag";

const { DirectoryTree: AntDirectoryTree } = Tree;

export interface DirectoryTreeComponentProps {
  treeData: FolderTreeData[];
  directoryIcons?: FolderTreeProps["directoryIcons"];
  selectedKeys?: string[];
  expandedKeys?: string[];
  onSelect?: TreeProps["onSelect"];
  onExpand?: TreeProps["onExpand"];
  showLine?: FolderTreeProps["showLine"];
  switcherIcon?: FolderTreeProps["switcherIcon"];
  defaultExpandAll?: boolean;
  className?: string;
  directoryTitle?: FolderTreeProps["directoryTitle"];
  moreActions?: FolderTreeProps["moreActions"];
  width?: number | string;
  draggable?: FolderTreeProps["draggable"];
  onDrop?: FolderTreeProps["onDrop"];
  /** 当前编辑节点信息 */
  editNode?: EditNodeInfo | null;
  /** 编辑值变化回调 */
  onEditValueChange?: (value: string) => void;
  /** 编辑确认回调 */
  onEditConfirm?: (value?: string) => void;
  /** 编辑取消回调 */
  onEditCancel?: () => void;
  /** 新建上下文 */
  createInfo?: CreateNodeContext | null;
  /** 开始重命名回调（由父组件注入） */
  onStartRename?: (fullPath: string, node: FolderTreeData) => void;
  /** 创建节点回调（由父组件注入） */
  onCreateNode?: (
    parentPath: string,
    options: { type: "file" | "folder"; defaultName?: string; extra?: any },
  ) => void;
}

interface InlineEditInputProps {
  editKey: string;
  value: string;
  className?: string;
  onValueChange?: (value: string) => void;
  onConfirm?: (value?: string) => void;
  onCancel?: () => void;
}

const InlineEditInput: React.FC<InlineEditInputProps> = ({
  editKey,
  value,
  className,
  onValueChange,
  onConfirm,
  onCancel,
}) => {
  const [draft, setDraft] = useState(value);
  const committedRef = useRef(false);

  useEffect(() => {
    setDraft(value);
    committedRef.current = false;
  }, [editKey, value]);

  const commit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    onValueChange?.(draft);
    onConfirm?.(draft);
  }, [draft, onConfirm, onValueChange]);

  return (
    <Input
      size="small"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") {
          committedRef.current = true;
          onCancel?.();
        }
      }}
      autoFocus
      className={className}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onSelect={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
    />
  );
};

const DirectoryTreeComponent: React.FC<DirectoryTreeComponentProps> = ({
  treeData,
  selectedKeys,
  expandedKeys,
  onSelect,
  onExpand,
  showLine = false,
  switcherIcon,
  defaultExpandAll = true,
  className,
  directoryIcons,
  directoryTitle,
  moreActions,
  width,
  draggable,
  onDrop,
  editNode,
  onEditValueChange,
  onEditConfirm,
  onEditCancel,
  createInfo,
  onStartRename,
  onCreateNode,
}) => {
  const styles = useStyles();

  const getIcon = useCallback(
    (node: FolderTreeData) => {
      if (directoryIcons === false || directoryIcons === null) {
        return null;
      }
      if (node.type == "folder") {
        const icon = directoryIcons?.directory;
        if (typeof icon === "function") return icon();
        return icon || <FolderOutlined />;
      }
      const filePath = node.path.toLowerCase();
      const extension = filePath.split(".").pop();
      if (extension) {
        const icon = directoryIcons?.[extension];
        if (icon) return typeof icon === "function" ? icon() : icon;
      }
      return <FileOutlined />;
    },
    [directoryIcons],
  );

  const buildPathSegments = useCallback(
    (node: FolderTreeData, parentSegments: string[] = []): string[] => {
      if (node.path === "/" && parentSegments.length === 0) {
        return ["/"];
      }
      return [...parentSegments, node.path].filter((s) => s !== "");
    },
    [],
  );

  const renderInlineInput = useCallback(
    (key: string, value: string) => (
      <span className={styles.treeNodeTitle}>
        <InlineEditInput
          editKey={key}
          value={value}
          className={styles.inlineInput}
          onValueChange={onEditValueChange}
          onConfirm={onEditConfirm}
          onCancel={onEditCancel}
        />
      </span>
    ),
    [onEditValueChange, onEditConfirm, onEditCancel, styles],
  );

  // ====== 拖拽模式 ======
  const dragMode = draggable === true ? "file" : draggable || false;

  // ====== 自定义拖拽 hook ======
  const handleDragDrop = useCallback(
    (dragNode: DataNode, dropNode: DataNode, dropPosition: DropPosition) => {
      const dragNd = dragNode as DataNode & { _originalNode?: FolderTreeData };
      const dropNd = dropNode as DataNode & { _originalNode?: FolderTreeData };
      const originalDragNode = dragNd._originalNode;
      const originalDropNode = dropNd._originalNode;
      if (!originalDragNode) return;

      const dragKey = String(dragNode.key);
      const dropKey = String(dropNode.key);
      const dragFileName = originalDragNode.path;
      let newPath: string;

      // 拖到底部 drop zone：放在根级别末尾
      if (!originalDropNode && dropKey === "__bottom_placeholder__") {
        newPath = dragFileName;
        onDrop?.({
          fileName: originalDragNode.title,
          extra: originalDragNode.extra,
          oldPath: dragKey,
          newPath,
          dragNode: originalDragNode,
          dropNode: { title: "", path: "" },
        });
        return;
      }

      if (!originalDropNode) return;

      if (!dropNode.isLeaf && dropPosition === 0) {
        // 拖入文件夹内部
        newPath = `${dropKey}/${dragFileName}`;
      } else {
        // 拖到节点旁边（同级）
        const dropParentPath = dropKey.includes("/")
          ? dropKey.substring(0, dropKey.lastIndexOf("/"))
          : "";
        newPath = dropParentPath
          ? `${dropParentPath}/${dragFileName}`
          : dragFileName;
      }

      onDrop?.({
        fileName: originalDragNode.title,
        extra: originalDragNode.extra,
        oldPath: dragKey,
        newPath,
        dragNode: originalDragNode,
        dropNode: originalDropNode,
      });
    },
    [onDrop],
  );

  const handleDragEnterExpand = useCallback(
    (nodeKey: string) => {
      const isExpanded = expandedKeys?.includes(nodeKey);
      if (!isExpanded) {
        onExpand?.([...(expandedKeys || []), nodeKey], {
          node: null as any,
          expanded: true,
          nativeEvent: null as any,
        });
      }
    },
    [expandedKeys, onExpand],
  );

  const {
    onDragStart,
    onDragEnd,
    onItemDragEnter,
    onItemDragOver,
    onItemDragLeave,
    onItemDrop,
    onContainerDragOver,
    onContainerDrop,
    canDragNode,
  } = useFolderDrag({
    dragMode,
    onDrop: handleDragDrop,
    onDragEnterExpand: handleDragEnterExpand,
  });

  // ====== 转换树数据 ======
  // 注意：convertToTreeData 不依赖拖拽状态，拖拽效果通过 DOM 操作实现
  const convertToTreeData = useCallback(
    (
      nodes: FolderTreeData[],
      parentSegments: string[] = [],
      parentKey: string = "",
    ): DataNode[] => {
      const result = nodes.map((node) => {
        const pathSegments = buildPathSegments(node, parentSegments);
        const fullPath = pathSegments.join("/").replace(/^\/+/, "");

        const isEditing = editNode && editNode.key === fullPath;

        const nodeChildren = node.children
          ? convertToTreeData(node.children, pathSegments, fullPath)
          : undefined;

        const baseNode: any = {
          key: fullPath,
          path: fullPath,
          pathSegments,
          icon: getIcon(node),
          isLeaf: !(node.type === "folder"),
          _originalNode: node,
          children: nodeChildren,
        };

        // 编辑态：直接渲染 inline input
        if (isEditing) {
          baseNode.title = renderInlineInput(
            editNode!.key,
            editNode!.currentValue,
          );
          return baseNode;
        }

        // title 内容（按钮、菜单等）
        const titleContent = (
          <span
            className={clsx(
              styles.treeNodeTitle,
              moreActions && styles.treeNodeTitleHover,
            )}
          >
            <span>{node.title}</span>
            {moreActions && (
              <Dropdown
                menu={moreActions(node, {
                  startRename: () => onStartRename?.(fullPath, node),
                  createNode: (options) => onCreateNode?.(fullPath, options),
                })}
                trigger={["click"]}
                placement="bottomRight"
              >
                <span
                  className={clsx(styles.moreIcon, "folder-tree-more-icon")}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreOutlined />
                </span>
              </Dropdown>
            )}
          </span>
        );

        // 启用拖拽时：在 title 外层包裹具有 data-node-key 属性的容器，
        // 用于 DOM 查询和事件绑定。拖拽状态通过 CSS class 控制。
        if (dragMode) {
          const nodeData = baseNode as DataNode & {
            _isTempNode?: boolean;
          };
          const draggableDiv = canDragNode(nodeData);

          baseNode.title = (
            <div
              className={clsx(
                styles.treeNodeWrapper,
                draggableDiv && styles.dragNodeHandle,
              )}
              data-node-key={fullPath}
              draggable={draggableDiv}
              onDragStart={
                draggableDiv ? (e) => onDragStart(e, baseNode) : undefined
              }
              onDragEnd={draggableDiv ? onDragEnd : undefined}
              onDragEnter={(e) => onItemDragEnter(e, baseNode)}
              onDragOver={(e) => onItemDragOver(e, baseNode)}
              onDragLeave={onItemDragLeave}
              onDrop={(e) => onItemDrop(e, baseNode)}
            >
              {titleContent}
            </div>
          );
        } else {
          baseNode.title = titleContent;
        }

        return baseNode;
      });

      // 新建模式：在目标父节点下插入临时节点
      if (createInfo && editNode && editNode.mode === "create") {
        const targetParentKey = createInfo.parentKey;
        if (
          targetParentKey === parentKey ||
          (targetParentKey === "" && parentKey === "") ||
          (targetParentKey === "/" && parentKey === "")
        ) {
          const tempNode: any = {
            key: editNode.key,
            title: renderInlineInput(editNode.key, editNode.currentValue),
            icon:
              createInfo.type === "folder" ? (
                <FolderOutlined />
              ) : (
                <FileOutlined />
              ),
            isLeaf: createInfo.type === "file",
            path: editNode.key,
            pathSegments: [editNode.key],
            _originalNode: null,
            _isTempNode: true,
          };
          result.push(tempNode);
        }
      }

      return result;
    },
    [
      buildPathSegments,
      getIcon,
      moreActions,
      styles,
      editNode,
      renderInlineInput,
      createInfo,
      onStartRename,
      onCreateNode,
      dragMode,
      canDragNode,
      onDragStart,
      onDragEnd,
      onItemDragEnter,
      onItemDragOver,
      onItemDragLeave,
      onItemDrop,
    ],
  );

  const treeDataConverted = useMemo(
    () => convertToTreeData(treeData, [], ""),
    [treeData, convertToTreeData],
  );

  const handleSelect: TreeProps["onSelect"] = useCallback(
    (keys: any, info: any) => {
      // 编辑态不响应选中
      if (editNode) return;
      // 临时节点不可选中
      const node = info.node as DataNode & { _isTempNode?: boolean };
      if (node._isTempNode) return;
      // 点击 moreActions 区域不触发选中
      const target = info.nativeEvent?.target as HTMLElement | null;
      if (
        target &&
        (target.closest(".folder-tree-more-icon") ||
          target.closest(".ant-dropdown"))
      ) {
        return;
      }
      onSelect?.(keys, info);
    },
    [onSelect, editNode],
  );

  const titleNode =
    directoryTitle === false || directoryTitle === null
      ? null
      : typeof directoryTitle === "function"
        ? directoryTitle()
        : directoryTitle;

  return (
    <div className={clsx(styles.directoryPanel, className)} style={{ width }}>
      {titleNode && <div className={styles.directoryTitle}>{titleNode}</div>}
      <div
        className={styles.directoryTreeContent}
        onDragOver={dragMode ? onContainerDragOver : undefined}
        onDrop={dragMode ? onContainerDrop : undefined}
      >
        <AntDirectoryTree
          style={
            {
              "--ant-tree-directory-node-selected-bg": "rgba(0,0,0,0.03)",
              "--ant-tree-directory-node-selected-color": "#1a1c1e",
            } as React.CSSProperties
          }
          treeData={treeDataConverted}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onSelect={handleSelect}
          onExpand={onExpand}
          multiple={false}
          blockNode
          showLine={showLine as any}
          switcherIcon={switcherIcon}
          defaultExpandAll={defaultExpandAll}
          draggable={false}
        />
      </div>
    </div>
  );
};

export default DirectoryTreeComponent;
